import { Dispatch, Fragment, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Popover, Tree, Typography } from 'antd';
import { useLocation } from 'react-router-dom';

import FilterItemSelected from './FilterItemSelected';
import { getStartEndType } from './pickers/DateTimeRangePicker';
import RefreshButton from './RefreshButton';
import RequestButton from './RequestButton';
import { LAZY_FILTER_NAMES } from './types';
import { PagesPathnames } from '../../../../../../app/types/ad';
import { findObjectById } from '../../../../../../helpers/utils/functions';
import {
  generateListKeys,
  getParentKey,
  prepareData,
} from '../../../../../../helpers/utils/reports';
import { injectUtmSectionsIntoFilters } from '../../../../../../helpers/utils/utmSections';
import Segments from '../../../../../../pages/dashboards/components/Params/filter/segments';
import {
  calendarTypes,
  data,
  updateFilters,
} from '../../../../constants/filters';
import { useActions } from '../../../../hooks/useActions';
import { useLoadGroupingsData } from '../../../../hooks/useLoadGroupingsData';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';
import { useUtmSections } from '../../../../hooks/useUtmSections';

interface IUniversalFilterProps {
  preElement?: ReactNode;
  isCompare: boolean;
  withSegment: boolean;
  disabled?: boolean;
  disableAttr?: boolean;
  filtersFilter?: string[];
  withLabel?: boolean;
  withRefresh?: boolean;
  isFixed?: boolean;
  setUpdateDashboard: () => void;
  fetchDashboardDataButtonVisible?: boolean,
  setFetchDashboardDataButtonVisible?: Dispatch<SetStateAction<boolean>>
}

/**
 * Рекурсивно фильтрует дерево, оставляя только узлы,
 * совпадающие с поиском, и их родителей.
 */
const filterTreeBySearch = (nodes: any[], search: string): any[] => {
  if (!search) return nodes;
  const reg = new RegExp(search, 'i');

  return nodes.reduce((acc: any[], node: any) => {
    const nameMatch = reg.test(node.name);

    if (node.children && node.children.length > 0) {
      const filteredChildren = filterTreeBySearch(node.children, search);
      if (nameMatch || filteredChildren.length > 0) {
        acc.push({ ...node, children: nameMatch ? node.children : filteredChildren });
      }
    } else {
      if (nameMatch) {
        acc.push(node);
      }
    }
    return acc;
  }, []);
};

// Базовый список ключей поиска по статическому дереву; секции UTM-меток
// динамические, поэтому в компоненте используется searchListKeys
export const listKeys = generateListKeys(data);

const DashboardFilter = ({
  preElement,
  isCompare,
  withSegment,
  filtersFilter,
  disabled = false,
  disableAttr = false,
  withLabel = true,
  withRefresh = false,
  isFixed = false,
  setUpdateDashboard,
  fetchDashboardDataButtonVisible = false,
  setFetchDashboardDataButtonVisible
}: IUniversalFilterProps) => {
  // Разделяем селекторы для предотвращения лишних ререндеров
  const filters = useTypedSelector((state) => state.filter.filters);
  const filterParent = useTypedSelector((state) => state.filter.filterParent);
  const { attribution, type_applications } = useTypedSelector(
    (state) => state.attributes.attributesDashboard
  );
  const period = useTypedSelector((state) => state.period);
  const { detailsDashboard } = useTypedSelector((state) => state.details);
  const { currentDashboard } = useTypedSelector((state) => state.reports);
  const locationState = useLocation();
  const {
    setMetaCompare,
    setFilterParent,
    setFilters,
    setCurrentSegment,
    setCurrentFunnel,
  } = useActions();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [autoExpandParent, setAutoExpandParent] = useState(true);
  const [treeData, setTreeData] = useState<any>([]);
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [rawData, setRawData] = useState<any[]>(data);

  // Lazy-загрузка доп. полей
  const { onLoadData } = useLoadGroupingsData(setRawData);

  // Секции разобранных UTM-меток регистрации — динамическая часть дерева фильтров
  const utmSections = useUtmSections();
  // Ключи для поиска считаем с учётом секций, иначе поиск не находит их пункты
  const searchListKeys = useMemo(
    () => generateListKeys(injectUtmSectionsIntoFilters(data, utmSections)),
    [ utmSections ]
  );

  // Пометка lazy-узлов (isLeaf: false) для корректной работы loadData
  const applyLazyFlags = useCallback((nodes: any[]): any[] => {
    if (!Array.isArray(nodes)) return nodes;
    return nodes.map((node: any) => {
      const hasChildren = Array.isArray(node.children) && node.children.length > 0;
      const isLazyParent = LAZY_FILTER_NAMES.has(node.name);
      return {
        ...node,
        isLeaf: !isLazyParent && !hasChildren,
        children: hasChildren ? applyLazyFlags(node.children) : node.children,
      };
    });
  }, []);

  useEffect(() => {
    if (isFixed) return;
    let data_ = rawData;
    if (location.pathname === '/users/ad-reports') {
      data_ = updateFilters();
    }
    // Достраиваем секции разобранных UTM-меток регистрации
    data_ = injectUtmSectionsIntoFilters(data_, utmSections);
    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    const _data = prepareData(
      filteredData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [''],
      '',
      filterParent
    );
    const url = new URL(window.location.href);
    const currentParams = url.searchParams.get('params');
    const paramsObject = currentParams ? JSON.parse(currentParams) : {};
    if (paramsObject?.filters) {
      let filtersFromParams: any[] = [];
      paramsObject.filters.forEach(
        (filt: { id: string; calendar: { type: string } }) => {
          let extraFields = {};
          if (calendarTypes.includes(filt?.calendar?.type)) {
            const { start, end } = getStartEndType({
              calendar: { type: filt?.calendar?.type },
            });
            extraFields = { ...extraFields, start, end };
          }
          const filterFromParam = {
            ...findObjectById(_data, filt.id),
            ...filt,
            ...extraFields,
          };
          if (!(filtersFromParams.some((item: any) => JSON.stringify(item) === JSON.stringify(filterFromParam)))) {
            filtersFromParams.push(filterFromParam);
          }
        }
      );
      setFilters([
        ...filtersFromParams,
        ...(currentDashboard?.values?.filters ?? []),
      ]);
    } else {
      setFilters([
        ...(currentDashboard?.values?.filters ?? []),
      ]);
    }
  }, [currentDashboard.id]);
  // Стабилизируем зависимости через JSON.stringify для предотвращения бесконечного цикла
  const filtersJson = JSON.stringify(filters);
  const filterParentJson = JSON.stringify(filterParent);
  const filtersFilterJson = JSON.stringify(filtersFilter);
  
  useEffect(() => {
    let data_ = rawData;
    if (location.pathname === '/users/ad-reports') {
      data_ = updateFilters();
    }
    // Достраиваем секции разобранных UTM-меток регистрации
    data_ = injectUtmSectionsIntoFilters(data_, utmSections);
    const filteredData = filtersFilter
      ? data_.filter((item: any) => filtersFilter.includes(item.name))
      : data_;
    const _data = prepareData(
      filteredData,
      searchValue,
      filters,
      disableAttr,
      '',
      '',
      '',
      '',
      [''],
      '',
      filterParent
    );

    if (_data.length === 1 && _data[0]?.key) {
      setAutoExpandParent(true);
      setExpandedKeys([_data[0].key]);
    }

    setTreeData(applyLazyFlags(_data));
  }, [
    searchValue,
    filtersJson,
    isCompare,
    filterParentJson,
    location.pathname,
    filtersFilterJson,
    disableAttr,
    rawData,
    applyLazyFlags,
    utmSections,
  ]);

  useEffect(() => {
    if (!searchValue) {
      let data_ = rawData;
      if (location.pathname === '/users/ad-reports') {
        data_ = updateFilters();
      }
      // Достраиваем секции разобранных UTM-меток регистрации
      data_ = injectUtmSectionsIntoFilters(data_, utmSections);
      setExpandedKeys([]);
      setAutoExpandParent(false);
      const filteredData = filtersFilter
        ? data_.filter((item: any) => filtersFilter.includes(item.name))
        : data_;
      const _data = prepareData(
        filteredData,
        searchValue,
        filters,
        disableAttr,
        '',
        '',
        '',
        '',
        [''],
        '',
        filterParent
      );

      setTreeData(applyLazyFlags(_data));
    }
    // Убран forceUpdate() - он не нужен, setTreeData уже триггерит ререндер
  }, [searchValue, utmSections]);

  // Убран useEffect с forceUpdate() - он вызывал бесконечный цикл ререндеров
  // React автоматически ререндерит компонент при изменении state/props

  useEffect(() => {
    if (!isCompare) {
      setMetaCompare({ value: false });
    }
  }, [isCompare]);

  const onExpand = (_expandedKeys: any) => {
    setExpandedKeys(_expandedKeys);
    setAutoExpandParent(false);
  };

  const onSearch = (e: any) => {
    const { value } = e.target;
    let expandedKeys = [];
    if (value !== '') {
      const reg = new RegExp(value, 'i');
      expandedKeys = searchListKeys
        .map((item: any) => {
          if (item.name.search(reg) > -1) {
            return getParentKey(item.id, treeData);
          }
          return null;
        })
        .filter((item: any, i: number, self: any) => item && self.indexOf(item) === i);
    }
    setExpandedKeys(expandedKeys);
    setSearchValue(value);
    setAutoExpandParent(true);
  };
  const handleSetBtnDisabled = useCallback((val: boolean) => {
    setBtnDisabled(val);
    setFetchDashboardDataButtonVisible && setFetchDashboardDataButtonVisible(val);
  }, [setFetchDashboardDataButtonVisible]);

  useEffect(() => {
    setVisible(false);
    setExpandedKeys([]);
    setSearchValue('');
  }, [filters]);

  // Логика показа кнопки "Показать" при изменении любых настроек
  useEffect(() => {
    handleSetBtnDisabled(false);
  }, [filters, attribution, type_applications, period, detailsDashboard, handleSetBtnDisabled]);

  const [visible, setVisible] = useState(false);

  const onClearFilters = () => {
    setCurrentSegment(null);
    setCurrentFunnel([]);
    setFilters([]);
    setFilterParent([]);
    handleSetBtnDisabled(false);
  };

  const isAutoRKTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    (Number(new URLSearchParams(location.search).get('tab')) === 1 ||
      !new URLSearchParams(location.search).get('tab'));

  const isAutoAdTab =
    locationState.pathname === PagesPathnames.autoPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 2;

  const isManualAdTab =
    locationState.pathname === PagesPathnames.manualPlacement &&
    Number(new URLSearchParams(location.search).get('tab')) === 1;

  // Фильтрация дерева при поиске — на уровне рендера
  const displayTreeData = useMemo(() => {
    const base = disableAttr
      ? treeData.filter((item: any) => item.name !== 'Реклама' && item.name !== 'Регистрации')
      : treeData;
    return searchValue ? filterTreeBySearch(base, searchValue) : base;
  }, [ treeData, searchValue, disableAttr ]);

  return (
    <div
      className={`params__filter ${isAutoRKTab ? 'filter-auto-rk-tab' : ''}
                  ${isAutoAdTab ? 'filter-auto-ad-tab' : ''}
                  ${isManualAdTab ? 'filter-manual-ad-tab' : ''}
                  ${isFixed && 'filter-fixed-block'}`}
      style={filters.length > 0 ? { flexWrap: 'wrap', } : {}}
    >
      {preElement}
      {withSegment && (
        <Segments disabled={disabled} />
      )}
      {withRefresh && <RefreshButton />}
      {filters.length > 0 && (
        <Button
          style={{ marginRight: 5 }}
          onClick={onClearFilters}
          icon={<CloseOutlined />}
          title="Очистить"
        />
      )}
      {withLabel && (
        <Typography.Text type="secondary" style={{ display: 'block', minWidth: 65 }}>
          Условия фильтра:&nbsp;
        </Typography.Text>
      )}

      {filters.length === 0
        ? null
        : filters.map((item: any, index: number) => {
          if (item.type === 'datetimerange') {
            if (!item.start && !item.end) {
              return null;
            }
          }

          let prefix = 'и';
          let secondPrefix = null;
          // Всегда используем условие "и" (AND) между всеми блоками фильтров
          // const prevIndex = index === 0 ? 0 : index - 1;
          // if (item.id && item.id === filters[prevIndex].id) {
          //   prefix = 'или';
          // }

          if (item.id?.includes('not_')) {
            secondPrefix = 'не';
          }

          return (
            <Fragment key={item.id ? `${item.id}-${index}` : index}>
              {index !== 0 && item.id && (
                <Typography.Text type="secondary">
                  &nbsp;{prefix}&nbsp;
                </Typography.Text>
              )}
              {secondPrefix ? (
                <b style={{ fontWeight: 600, color: '#ff0000' }}>
                  {secondPrefix}&nbsp;
                </b>
              ) : (
                ''
              )}
              {
                <FilterItemSelected
                  arrayIndex={index}
                  id={item.id}
                  itemData={item}
                  full_name={item.full_name}
                  {...item}
                />
              }
            </Fragment>
          );
        })}
      <span>
        <Popover
          content={
            <Tree.DirectoryTree
              blockNode
              switcherIcon={<DownOutlined />}
              showIcon={false}
              selectable={false}
              treeData={displayTreeData}
              onExpand={onExpand}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              loadData={onLoadData}
              key={'name'}
            />
          }
          title={
            <Input
              onChange={(e) => onSearch(e)}
              placeholder="Найти условие"
              allowClear
            />
          }
          trigger="click"
          placement="bottom"
          overlayClassName="filter-popover"
          open={visible}
          onOpenChange={setVisible}
        >
          <Button icon={<PlusOutlined />} type="primary" shape="circle" />
        </Popover>
        <RequestButton setUpdateDashboard={setUpdateDashboard} btnDisabled={btnDisabled} setBtnDisabled={handleSetBtnDisabled} />
      </span>
    </div>
  );

};

export default DashboardFilter;